import { useState, useEffect } from 'react'
import {
  Building2,
  Plus,
  MapPin,
  Users,
  Plane,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Phone,
  Mail,
  Star,
  Shield,
  Wrench,
  CheckCircle2,
  Info,
  X,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import api from '@/services/api'

// ── Interfaces ──────────────────────────────────────────────────────────────

type CenterStatus = 'active' | 'inactive' | 'maintenance'

interface ServiceCenter {
  id: string
  name: string
  location: string
  address: string
  phone: string
  email: string
  website?: string
  aircraftCount: number
  technicianCount: number
  status: CenterStatus
  isPrimary: boolean
  certifications: string[]
  capabilities: string[]
  assignedAircraft: FleetAircraftSummary[]
}

interface FleetAircraftSummary {
  tailNumber: string
  model: string
  currentHours: number
  status: 'active' | 'in-maintenance' | 'aog'
}

interface AddCenterForm {
  name: string
  location: string
  address: string
  phone: string
  email: string
  website: string
}

// ── Sample Data ─────────────────────────────────────────────────────────────

const sampleCenters: ServiceCenter[] = [
  {
    id: '1',
    name: 'Honda Aircraft Company',
    location: 'Greensboro, NC',
    address: '6430 Burnt Poplar Rd, Greensboro, NC 27409',
    phone: '+1 (336) 662-0246',
    email: 'service@hondaaircraft.com',
    website: 'https://www.hondaaircraft.com',
    aircraftCount: 3,
    technicianCount: 45,
    status: 'active',
    isPrimary: true,
    certifications: ['FAA Part 145 Repair Station', 'EASA Part 145', 'Honda Authorized Service Center', 'CAMP Systems Provider'],
    capabilities: ['Phased Inspections', '600-Hr Major', '1200-Hr Extended', 'Avionics (Garmin G3000)', 'Composite Repair', 'Paint', 'Interior', 'GE Honda HF120 Engine'],
    assignedAircraft: [
      { tailNumber: 'N420HJ', model: 'HA-420 Elite S', currentHours: 487.3, status: 'active' },
      { tailNumber: 'N421HA', model: 'HA-420 Elite II', currentHours: 142.8, status: 'active' },
      { tailNumber: 'N600HC', model: 'HA-420 Classic', currentHours: 1187.0, status: 'in-maintenance' },
    ],
  },
  {
    id: '2',
    name: 'Cutter Aviation',
    location: 'Phoenix, AZ',
    address: '2802 E Old Tower Rd, Phoenix, AZ 85034',
    phone: '+1 (602) 273-1237',
    email: 'maintenance@cutteraviation.com',
    website: 'https://www.cutteraviation.com',
    aircraftCount: 1,
    technicianCount: 12,
    status: 'active',
    isPrimary: false,
    certifications: ['FAA Part 145 Repair Station', 'Honda Authorized Service Center'],
    capabilities: ['Phased Inspections', '600-Hr Major', 'Avionics', 'GE Honda HF120 Engine Line Maintenance'],
    assignedAircraft: [
      { tailNumber: 'N523HE', model: 'HA-420 Elite', currentHours: 291.4, status: 'active' },
    ],
  },
  {
    id: '3',
    name: 'West Star Aviation',
    location: 'East Alton, IL',
    address: '1 Mid America Airport Center, East Alton, IL 62024',
    phone: '+1 (618) 258-3756',
    email: 'scheduling@weststaraviation.com',
    website: 'https://www.weststaraviation.com',
    aircraftCount: 1,
    technicianCount: 18,
    status: 'active',
    isPrimary: false,
    certifications: ['FAA Part 145 Repair Station', 'Honda Authorized Service Center', 'EASA Part 145'],
    capabilities: ['Phased Inspections', '600-Hr Major', '1200-Hr Extended', 'Avionics', 'Paint', 'Interior Refurbishment'],
    assignedAircraft: [
      { tailNumber: 'N789HJ', model: 'HA-420 Elite S', currentHours: 605.2, status: 'in-maintenance' },
    ],
  },
  {
    id: '4',
    name: 'Banyan Air Service',
    location: 'Fort Lauderdale, FL',
    address: '5360 NW 20th Terrace, Fort Lauderdale, FL 33309',
    phone: '+1 (954) 491-3170',
    email: 'mx@banyanair.com',
    website: 'https://www.banyanair.com',
    aircraftCount: 1,
    technicianCount: 22,
    status: 'active',
    isPrimary: false,
    certifications: ['FAA Part 145 Repair Station', 'Honda Authorized Service Center'],
    capabilities: ['Phased Inspections', '600-Hr Major', 'Avionics (Garmin G3000)', 'GE Honda HF120 Engine Line Maintenance'],
    assignedAircraft: [
      { tailNumber: 'N600HC', model: 'HA-420 Classic', currentHours: 1187.0, status: 'active' },
    ],
  },
  {
    id: '5',
    name: 'Skyservice Business Aviation',
    location: 'Toronto, ON, Canada',
    address: '30 Convair Dr E, Toronto, ON M3K 1Y4, Canada',
    phone: '+1 (416) 635-4657',
    email: 'maintenance@skyservice.com',
    website: 'https://www.skyservice.com',
    aircraftCount: 0,
    technicianCount: 8,
    status: 'inactive',
    isPrimary: false,
    certifications: ['Transport Canada AMO', 'Honda Authorized Service Center', 'EASA Part 145'],
    capabilities: ['Phased Inspections', '600-Hr Major', 'Avionics'],
    assignedAircraft: [],
  },
]

const emptyForm: AddCenterForm = {
  name: '',
  location: '',
  address: '',
  phone: '',
  email: '',
  website: '',
}

// ── Component ───────────────────────────────────────────────────────────────

export function ServiceCentersPage() {
  const [centers, setCenters] = useState<ServiceCenter[]>(sampleCenters)
  const [loading, setLoading] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [selectedCenter, setSelectedCenter] = useState<ServiceCenter | null>(null)
  const [form, setForm] = useState<AddCenterForm>({ ...emptyForm })
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    const fetchCenters = async () => {
      setLoading(true)
      try {
        const data = await api.get<ServiceCenter[]>('/api/service-centers')
        if (data && Array.isArray(data)) {
          setCenters(data)
        }
      } catch {
        // Use sample data on API error
      } finally {
        setLoading(false)
      }
    }
    fetchCenters()
  }, [])

  const handleAddCenter = async () => {
    if (!form.name || !form.location) return

    setSubmitting(true)
    const newCenter: ServiceCenter = {
      id: String(Date.now()),
      name: form.name,
      location: form.location,
      address: form.address,
      phone: form.phone,
      email: form.email,
      website: form.website || undefined,
      aircraftCount: 0,
      technicianCount: 0,
      status: 'active',
      isPrimary: false,
      certifications: [],
      capabilities: [],
      assignedAircraft: [],
    }

    try {
      await api.post('/api/service-centers', {
        name: form.name,
        location: form.location,
        address: form.address,
        phone: form.phone,
        email: form.email,
        website: form.website,
      })
    } catch {
      // Continue with local state update
    }

    setCenters([...centers, newCenter])
    setForm({ ...emptyForm })
    setShowAddForm(false)
    setSubmitting(false)
  }

  const getStatusBadge = (status: CenterStatus) => {
    switch (status) {
      case 'active':
        return <Badge variant="success">Active</Badge>
      case 'inactive':
        return <Badge variant="secondary">Inactive</Badge>
      case 'maintenance':
        return <Badge variant="warning">Under Maintenance</Badge>
    }
  }

  const getAircraftStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="success" className="text-xs">Active</Badge>
      case 'in-maintenance':
        return <Badge variant="warning" className="text-xs">In Maintenance</Badge>
      case 'aog':
        return <Badge variant="destructive" className="text-xs">AOG</Badge>
      default:
        return <Badge variant="secondary" className="text-xs">{status}</Badge>
    }
  }

  const totalAircraft = centers.reduce((sum, c) => sum + c.aircraftCount, 0)
  const totalTechnicians = centers.reduce((sum, c) => sum + c.technicianCount, 0)
  const activeCenters = centers.filter((c) => c.status === 'active').length

  // Detail View
  if (selectedCenter) {
    return (
      <div className="p-6 lg:p-8 space-y-6">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => setSelectedCenter(null)}>
          <ChevronLeft className="mr-2 h-4 w-4" />
          Back to Service Centers
        </Button>

        {/* Center Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4">
            <div className={`rounded-xl p-3 ${selectedCenter.isPrimary ? 'bg-blue-600' : 'bg-gray-600'}`}>
              <Building2 className="h-8 w-8 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900">{selectedCenter.name}</h1>
                {selectedCenter.isPrimary && (
                  <Badge variant="info" className="gap-1">
                    <Star className="h-3 w-3" />
                    Primary / HQ
                  </Badge>
                )}
              </div>
              <p className="text-gray-600 flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {selectedCenter.address}
              </p>
            </div>
          </div>
          {getStatusBadge(selectedCenter.status)}
        </div>

        {/* Contact Info */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                  <p className="text-sm font-medium">{selectedCenter.phone}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="text-sm font-medium">{selectedCenter.email}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Plane className="h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Aircraft Assigned</p>
                  <p className="text-sm font-medium">{selectedCenter.aircraftCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Users className="h-4 w-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Technicians</p>
                  <p className="text-sm font-medium">{selectedCenter.technicianCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Certifications */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-500" />
                Certifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {selectedCenter.certifications.map((cert, idx) => (
                  <div key={idx} className="flex items-center gap-2 rounded-lg border p-2.5">
                    <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                    <span className="text-sm text-gray-700">{cert}</span>
                  </div>
                ))}
                {selectedCenter.certifications.length === 0 && (
                  <p className="text-sm text-gray-500">No certifications listed.</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Capabilities */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Wrench className="h-5 w-5 text-purple-500" />
                Capabilities
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {selectedCenter.capabilities.map((cap, idx) => (
                  <Badge key={idx} variant="outline">
                    {cap}
                  </Badge>
                ))}
                {selectedCenter.capabilities.length === 0 && (
                  <p className="text-sm text-gray-500">No capabilities listed.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Assigned Fleet */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Plane className="h-5 w-5 text-gray-500" />
              Fleet Assigned to This Center
            </CardTitle>
            <CardDescription>
              Aircraft currently managed by {selectedCenter.name}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {selectedCenter.assignedAircraft.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Tail Number</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Model</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">Current Hours</th>
                      <th className="px-4 py-3 text-center font-medium text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCenter.assignedAircraft.map((aircraft) => (
                      <tr key={aircraft.tailNumber} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono font-semibold">{aircraft.tailNumber}</td>
                        <td className="px-4 py-3 text-gray-700">{aircraft.model}</td>
                        <td className="px-4 py-3 text-right font-mono">{aircraft.currentHours.toFixed(1)}</td>
                        <td className="px-4 py-3 text-center">{getAircraftStatusBadge(aircraft.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                <Plane className="h-8 w-8 text-gray-300 mb-2" />
                <p className="text-sm">No aircraft currently assigned to this center.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Map Placeholder */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="h-5 w-5 text-red-500" />
              Location
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-8 text-center">
              <MapPin className="mx-auto h-8 w-8 text-gray-300 mb-2" />
              <p className="font-medium text-gray-700">{selectedCenter.name}</p>
              <p className="text-sm text-gray-500">{selectedCenter.address}</p>
              <p className="mt-2 text-xs text-gray-400">
                Interactive map integration available in a future release
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // ── List View (Default) ─────────────────────────────────────────────────

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Service Centers</h1>
          <p className="text-gray-600">Manage HondaJet authorized service center locations</p>
        </div>
        <Button onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? (
            <>
              <X className="mr-2 h-4 w-4" />
              Cancel
            </>
          ) : (
            <>
              <Plus className="mr-2 h-4 w-4" />
              Add Service Center
            </>
          )}
        </Button>
      </div>

      {/* Access Notice */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="mt-0.5 h-5 w-5 text-blue-600 shrink-0" />
            <p className="text-sm text-blue-700">
              <span className="font-medium text-blue-900">Admin View:</span>{' '}
              You are viewing all service centers. Technicians see only their assigned center and its fleet.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Centers</p>
                <p className="text-2xl font-bold">{centers.length}</p>
              </div>
              <Building2 className="h-8 w-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Centers</p>
                <p className="text-2xl font-bold text-green-600">{activeCenters}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Aircraft</p>
                <p className="text-2xl font-bold">{totalAircraft}</p>
              </div>
              <Plane className="h-8 w-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Technicians</p>
                <p className="text-2xl font-bold">{totalTechnicians}</p>
              </div>
              <Users className="h-8 w-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add Service Center Form */}
      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Add New Service Center
            </CardTitle>
            <CardDescription>
              Register a new HondaJet authorized service center
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Center Name</label>
                  <Input
                    placeholder="Service Center Name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Location (City, State)</label>
                  <Input
                    placeholder="City, State"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">Full Address</label>
                <Input
                  placeholder="Street address, City, State ZIP"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Phone</label>
                  <Input
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Email</label>
                  <Input
                    type="email"
                    placeholder="service@example.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Website (optional)</label>
                  <Input
                    type="url"
                    placeholder="https://www.example.com"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddForm(false)
                    setForm({ ...emptyForm })
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddCenter}
                  disabled={submitting || !form.name || !form.location}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Center
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Map Placeholder */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="h-5 w-5 text-red-500" />
            Service Center Locations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-6">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {centers.filter((c) => c.status === 'active').map((center) => (
                <div
                  key={center.id}
                  className="flex items-start gap-2 rounded-lg bg-white p-3 border cursor-pointer hover:shadow-sm transition-shadow"
                  onClick={() => setSelectedCenter(center)}
                >
                  <MapPin className={`h-4 w-4 mt-0.5 shrink-0 ${center.isPrimary ? 'text-blue-600' : 'text-red-500'}`} />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{center.name}</p>
                    <p className="text-xs text-gray-500">{center.location}</p>
                    <p className="text-xs text-gray-400">{center.aircraftCount} aircraft | {center.technicianCount} technicians</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-center text-gray-400">
              Interactive map visualization available in a future release
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Service Centers List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-500">Loading service centers...</span>
        </div>
      ) : (
        <div className="space-y-4">
          {centers.map((center) => (
            <Card
              key={center.id}
              className={`cursor-pointer hover:shadow-md transition-shadow ${
                center.isPrimary ? 'border-blue-200 ring-1 ring-blue-100' : ''
              }`}
              onClick={() => setSelectedCenter(center)}
            >
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    <div className={`rounded-xl p-2.5 shrink-0 ${center.isPrimary ? 'bg-blue-100' : 'bg-gray-100'}`}>
                      <Building2 className={`h-6 w-6 ${center.isPrimary ? 'text-blue-600' : 'text-gray-600'}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{center.name}</h3>
                        {center.isPrimary && (
                          <Badge variant="info" className="gap-1">
                            <Star className="h-3 w-3" />
                            Primary / HQ
                          </Badge>
                        )}
                        {getStatusBadge(center.status)}
                      </div>
                      <p className="mt-1 text-sm text-gray-500 flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5 shrink-0" />
                        {center.location}
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Plane className="h-3 w-3" />
                          {center.aircraftCount} aircraft
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {center.technicianCount} technicians
                        </span>
                        <span className="flex items-center gap-1">
                          <Shield className="h-3 w-3" />
                          {center.certifications.length} certifications
                        </span>
                      </div>
                      {/* Capabilities Preview */}
                      <div className="mt-2 flex flex-wrap gap-1">
                        {center.capabilities.slice(0, 4).map((cap, idx) => (
                          <span
                            key={idx}
                            className="inline-flex rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600"
                          >
                            {cap}
                          </span>
                        ))}
                        {center.capabilities.length > 4 && (
                          <span className="inline-flex rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">
                            +{center.capabilities.length - 4} more
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="shrink-0"
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedCenter(center)
                    }}
                  >
                    View Details
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}

          {centers.length === 0 && (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Building2 className="h-12 w-12 text-gray-300" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">No service centers</h3>
                <p className="mt-1 text-sm text-gray-500">Add your first service center to get started.</p>
                <Button className="mt-4" onClick={() => setShowAddForm(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Service Center
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  )
}
